import type {
  AudienceDefinition,
  AudienceDelivery,
  AudienceDestination,
  AudienceSnapshot
} from '../types';
import {
  type DerivedAudienceStatus,
  deriveAudienceStatus,
  isInProgressStatus,
  isLiveDestination,
  UNKNOWN_STATUS
} from './deriveAudienceStatus';
import { isTransientStatus, type LoadError } from './loadErrorMessage';

/** One viewer row: a definition joined to its latest run and its destinations. */
export type AudienceRowModel = {
  definition: AudienceDefinition;
  latestSnapshot: AudienceSnapshot | null;
  destinations: AudienceDestination[];
  liveDestinations: AudienceDestination[];
  derived: DerivedAudienceStatus;
  /** Why a lookup this row depends on failed on the last load. */
  loadError: string | null;
  /** `derived` comes from the last good data, since the latest load of it failed. */
  stale: boolean;
  /** `derived` is Unknown and a later poll could load it, since a lookup it waits on failed transiently. */
  unknownIsTransient: boolean;
  /** The destinations lookup itself failed, so the delivery column has nothing to show. */
  destinationsUnavailable: boolean;
};

export type BuildAudienceRowsInput = {
  definitions: AudienceDefinition[];
  latestSnapshotByDefinitionId: Record<number, AudienceSnapshot | null>;
  latestSnapshotErrorByDefinitionId: Record<number, LoadError>;
  destinationsByDefinitionId: Record<number, AudienceDestination[]>;
  destinationsErrorByDefinitionId: Record<number, LoadError>;
  deliveriesBySnapshotId: Record<number, AudienceDelivery[]>;
  deliveriesErrorBySnapshotId: Record<number, LoadError>;
};

export const buildAudienceRows = (input: BuildAudienceRowsInput): AudienceRowModel[] =>
  input.definitions.map(definition => {
    const latestSnapshot = input.latestSnapshotByDefinitionId[definition.id] ?? null;
    const destinations = input.destinationsByDefinitionId[definition.id] ?? [];
    const deliveries = latestSnapshot
      ? (input.deliveriesBySnapshotId[latestSnapshot.id] ?? [])
      : [];
    const destinationsError = input.destinationsErrorByDefinitionId[definition.id];
    const status = deriveStatusFromLookups(definition, latestSnapshot, destinations, deliveries, [
      {
        error: input.latestSnapshotErrorByDefinitionId[definition.id],
        hasLastGood: definition.id in input.latestSnapshotByDefinitionId
      },
      {
        error: destinationsError,
        hasLastGood: definition.id in input.destinationsByDefinitionId
      },
      {
        error: latestSnapshot ? input.deliveriesErrorBySnapshotId[latestSnapshot.id] : undefined,
        hasLastGood: latestSnapshot != null && latestSnapshot.id in input.deliveriesBySnapshotId
      }
    ]);
    return {
      definition,
      latestSnapshot,
      destinations,
      liveDestinations: destinations.filter(isLiveDestination),
      ...status,
      destinationsUnavailable:
        destinationsError != null && !(definition.id in input.destinationsByDefinitionId)
    };
  });

/** One lookup a status depends on: its failure on the last load, and whether an earlier load of it succeeded. */
export type StatusLookup = { error: LoadError | null | undefined; hasLastGood: boolean };

export type StatusFromLookups = {
  derived: DerivedAudienceStatus;
  loadError: string | null;
  stale: boolean;
  unknownIsTransient: boolean;
};

/**
 * The status of an audience whose lookups may have failed.
 *
 * - A failed lookup with last good data keeps the status derived from that data, marked `stale`.
 * - A failed lookup that never loaded makes an active definition Unknown.
 * - A paused or deleted definition's status does not depend on the lookups.
 * - `loadError` is the first failed lookup's message.
 * - `unknownIsTransient` is true when the status is Unknown and a lookup that never loaded failed with `isTransientStatus`.
 */
export const deriveStatusFromLookups = (
  definition: AudienceDefinition,
  latestSnapshot: AudienceSnapshot | null,
  destinations: AudienceDestination[],
  deliveries: AudienceDelivery[],
  lookups: StatusLookup[]
): StatusFromLookups => {
  const failed = lookups.filter(lookup => lookup.error);
  const loadError = failed[0]?.error?.message ?? null;
  const neverLoaded = failed.filter(lookup => !lookup.hasLastGood);
  const unknown = definition.status === 'active' && neverLoaded.length > 0;
  return {
    derived: unknown
      ? UNKNOWN_STATUS
      : deriveAudienceStatus(definition, latestSnapshot, destinations, deliveries),
    loadError,
    stale: loadError != null && !unknown,
    unknownIsTransient:
      unknown && neverLoaded.some(lookup => isTransientStatus(lookup.error?.status ?? null))
  };
};

/**
 * Whether a page showing this status keeps polling.
 *
 * - An in-progress status polls, including one kept as the last known status.
 * - An Unknown status polls only while a lookup it waits on failed transiently.
 */
export const statusKeepsPolling = (
  status: Pick<StatusFromLookups, 'derived' | 'unknownIsTransient'>
): boolean => isInProgressStatus(status.derived.status) || status.unknownIsTransient;

/** Hover text for a status pill: why it is Unknown, or that it is the last known status. */
export const statusHoverText = (status: {
  derived: DerivedAudienceStatus;
  loadError: string | null;
  stale: boolean;
}): string | undefined => {
  if (!status.loadError) return undefined;
  if (status.derived.status === 'Unknown') return `Status unknown: ${status.loadError}`;
  return status.stale
    ? `Last known status. The latest refresh failed: ${status.loadError}`
    : undefined;
};
